/*eslint new-cap:0 */
import ko from 'knockout';
import _ from 'underscore';
import { Ad } from '@bingads-webui-campaign/campaign-ui-schemas';
import koSchema from '@bingads-webui-component-legacy/ko-schema/index';
import { types as campaignTypes } from '@bingads-webui-campaign-legacy/adgroup-create-advanced/util/campaignType';
import config from '@bingads-webui-component-legacy/config/index';
import pageContext from '@bingads-webui-static-legacy/page-context';
import { isAudienceNativeAdsCampaign, isAudienceCampaign } from '@bingads-webui-campaign-react/constants';
import { AdFormat } from '@bingads-webui-campaign-react/display-plus-native-ads';
import { setClarityTag } from '@bingads-webui-universal/clarity';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

const adGroupTypeEnum = {
  SearchStandard: 'SearchStandard',
  SearchDynamic: 'SearchDynamic',
};

const adTypeEnum = {
  text: 'Text',
  dynamicSearch: 'DynamicSearch',
  appInstall: 'AppInstall',
  product: 'Product',
  expandedText: 'ExpandedText',
  responsive: 'Responsive',
  responsiveSearch: 'ResponsiveSearch',
};

const defaultAdType = _.object(
  [
    campaignTypes.search,
    campaignTypes.shopping,
    campaignTypes.audience,
    campaignTypes.hotel,
  ],
  [
    ({
      isRSAAsDefaultTypeEnabled,
    }) => {
      if (config.get('CampaignObjective') === 'AppInstalls') {
        return adTypeEnum.appInstall;
      }

      if (isRSAAsDefaultTypeEnabled) {
        return adTypeEnum.responsiveSearch;
      }

      return adTypeEnum.expandedText;
    },
    _.constant(adTypeEnum.product),
    _.constant(adTypeEnum.responsive),
    _.constant(null),
  ],
);

const campaignAdTypesMapping = _.object(
  [
    campaignTypes.search,
    campaignTypes.shopping,
    campaignTypes.audience,
    campaignTypes.hotel,
  ],
  [
    ({
      appInstallAdsAllowed,
      isSTADisabled,
      isRSAAsDefaultTypeEnabled,
      isForVNext,
    }) => {

      let base = [];

      if (!isSTADisabled) {
        base.push(adTypeEnum.text);
      }

      if (appInstallAdsAllowed) {
        base.push(adTypeEnum.appInstall);
      }

      if (isForVNext && pageContext.Permissions.IsMultiMediaAdsEnabled &&
        !pageContext.Permissions.IsAdultAdsEnabled &&
        (!pageContext.Permissions.IsDomesticSimplifiedChineseEnabled || (pageContext.Permissions.IsDomesticSimplifiedChineseEnabled && pageContext.Permissions.dynamic.MMAForCN))
      ) {
        // Under a search campaign, responsive ad represents multimedia ad
        base.push(adTypeEnum.responsive);
      }

      if (pageContext.Permissions.IsWhiteListEXTAEnabled) {
        base = _.union([adTypeEnum.expandedText], base);
      }
      if (isRSAAsDefaultTypeEnabled) {
        base.unshift(adTypeEnum.responsiveSearch);
      } else {
        base.push(adTypeEnum.responsiveSearch);
      }

      return base;
    },
    _.constant([adTypeEnum.product]),
    _.constant([adTypeEnum.responsive]),
    _.constant([]),
  ],
);

const permissions = config.get('Permissions');
const { IsResponsiveAdForSSCEnabled } = permissions;
const campaignSubTypeAdTypesMapping = _.object(
  [
    'ShoppingSmartAds',
  ],
  [
    _.constant(IsResponsiveAdForSSCEnabled ? [adTypeEnum.product, adTypeEnum.responsive] : [adTypeEnum.product]),
  ],
);

export default class ViewModel {
  constructor({
    model = null,
    campaignId = null,
    adGroupId = null,
    adGroupType = null,
    campaignType = null,
    campaignSubType = null,
    isFeedBasedCampaign = null,
    showInline = false,
    appInstallAdsAllowed = false,
    campaignDomain = '',
    isDynamicDescriptionEnabled = false,
    isMigratingToEXTA = false,
    isSTADisabled = false,
    isRSAAsDefaultTypeEnabled = false,
    isResponsiveAdEnabled = false,
    isMMAInDSAEnabled = false,
    isForVNext = false,
    adsCountMap = {},
    isAdgroupCreation = false,
    selectedAdFormat = undefined,
  } = {}) {
    this.model = model;
    this.showInline = showInline;
    this.appInstallAdsAllowed = appInstallAdsAllowed;
    this.isMigratingToEXTA = isMigratingToEXTA;
    this.isSTADisabled = isSTADisabled;
    this.isRSAAsDefaultTypeEnabled = isRSAAsDefaultTypeEnabled;
    this.isResponsiveAdEnabled = isResponsiveAdEnabled;
    this.isMMAInDSAEnabled = isMMAInDSAEnabled;
    this.isForVNext = isForVNext;
    this.isAdgroupCreation = isAdgroupCreation;
    this.campaignType = campaignType;
    this.campaignSubType = campaignSubType;
    this.selectedAdFormat = ko.observable(selectedAdFormat);

    if (!this.isResponsiveAdEnabled && campaignAdTypesMapping[campaignTypes.audience]) {
      delete campaignAdTypesMapping[campaignTypes.audience];
    }

    this.scope = ko.observable({
      campaignId,
      adGroupId,
      adGroupType,
      campaignType,
      campaignSubType,
      campaignDomain,
      isDynamicDescriptionEnabled,
      isFeedBasedCampaign,
      adsCountMap,
      scenario: 'ads-create',
    });

    if (!this.showInline) {
      this.hasWebsiteImageOptedInObservable = ko.observable(true);
    }

    this.type = ko.computed(() => {
      let type = null;

      if (_.isEmpty(model)) {
        if (this.scope().campaignType) {
          type = defaultAdType[this.scope().campaignType]({
            isRSAAsDefaultTypeEnabled: this.isRSAAsDefaultTypeEnabled,
          });
        } else if (isRSAAsDefaultTypeEnabled) {
          type = adTypeEnum.responsiveSearch;
        } else {
          type = adTypeEnum.expandedText;
        }
      } else {
        type = model.Type;
        if (isMigratingToEXTA && model.Type === adTypeEnum.text) {
          type = adTypeEnum.expandedText;
        }
      }

      return type;
    });

    koSchema.properties(this, Ad, ['Type'], {
      Type: this.type,
    });

    this.adGroupType = ko.computed(() => {
      return this.scope().adGroupType || adGroupType;
    });

    this.availableAdTypes = this.getAvailableAdTypes();

    this.adTypesRegisterForLanguageChange = _.intersection(this.availableAdTypes, [adTypeEnum.text, adTypeEnum.expandedText]);

    this.adTypeOptions = ko.computed(() => {
      let adTypes = [];

      if (!_.isEmpty(this.model)) {
        adTypes = this.getTypesFromExistingModel(this.model);
      } else if (this.isSmartShoppingCampaign()) {
        adTypes = campaignSubTypeAdTypesMapping.ShoppingSmartAds();
      } else if (this.scope().campaignType) {
        adTypes = campaignAdTypesMapping[this.scope().campaignType]({
          appInstallAdsAllowed: this.appInstallAdsAllowed,
          isSTADisabled: this.isSTADisabled,
          isRSAAsDefaultTypeEnabled: this.isRSAAsDefaultTypeEnabled,
          isForVNext: this.isForVNext,
        });
      }

      if (this.isSmartShoppingCampaign() && this.scope().adsCountMap) {
        // filter ad type which already exists under SSC ad group
        adTypes = _.filter(adTypes, (adType) => !this.scope().adsCountMap[this.scope().adGroupId] || !this.scope().adsCountMap[this.scope().adGroupId][`${adType}`]);
      }

      if (this.isDynamicAdGroupAdCreation()) {
        adTypes = this.getDynamicSearchAdTypes();
      }

      return _.map(adTypes, (adType) => {
        return {
          Name: adType === adTypeEnum.responsive && this.scope().campaignType === 1 ?
            i18n.get('AdTypeOption_Multimedia') : i18n.get(`AdType_${adType}Ad`),
          Value: adType,
        };
      });
    });

    this.showAdType = ko.computed(() => {
      return _.size(this.adTypeOptions()) > 1;
    });

    this.showAdTypeWithErrors = ko.computed(() => {
      return this.showAdType() || (!showInline && this.errors().length > 0);
    });

    this.errors.isAppealable = ko.observable(false);
    this.disableAdType = ko.observable(!_.isEmpty(model));

    this.showMigrationAlertForEXTA = ko.computed(() => {
      return isMigratingToEXTA && (this.Type() === adTypeEnum.expandedText);
    });
    this.showMigrationAlertForSTA = ko.computed(() => {
      return isMigratingToEXTA && (this.Type() === adTypeEnum.text);
    });
    this.showSTAEndWarning = ko.computed(() => {
      return !isMigratingToEXTA && (this.Type() === adTypeEnum.text && showInline);
    });

    this.isDisplayPlusNativeEdit = ko.observable(
      this.campaignType && this.campaignSubType && this.scope().adGroupId ?
        isAudienceCampaign(this.campaignType) && isAudienceNativeAdsCampaign(this.campaignSubType) && permissions.dynamic && permissions.dynamic.DisplayPlusEdit :
        false);

    if (this.isDisplayPlusNativeEdit()) {
      setClarityTag({
        IsDisplayPlusAudienceNative: true,
        CampaignSubType: this.campaignSubType,
      });
    }

    this.containerClass = ko.computed(() => {
      return this.isDisplayPlusNativeEdit() ? 'display-plus-ad-editor-form__container' : 'ad-editor-form__container';
    });

    this.hideFooter = ko.computed(() => {
      return this.selectedAdFormat() === AdFormat.Display || this.selectedAdFormat() === AdFormat.Video;
    });

    this.showAdFormatOptions = ko.computed(() => {
      return permissions.dynamic && permissions.dynamic.DisplayPlusEdit && permissions.dynamic.MultiAdFormat && isAudienceNativeAdsCampaign(this.scope().campaignSubType || this.campaignSubType);
    });

    return this;
  }

  isDynamicAdGroupAdCreation() {
    const isDSAMixedModeCampaignEnabled = _.result(config.get('Permissions'), 'IsDSAMixedModeCampaignEnabled', false);

    return isDSAMixedModeCampaignEnabled &&
      this.scope().campaignType === campaignTypes.search &&
      this.adGroupType() === adGroupTypeEnum.SearchDynamic;
  }

  getDynamicSearchAdTypes() {
    let base = [adTypeEnum.dynamicSearch];

    if (this.isMMAInDSAEnabled && !this.isAdgroupCreation) {
      base.push(adTypeEnum.responsive);
    }
    return base;
  }

  getAvailableAdTypes() {
    if (!_.isEmpty(this.model)) {
      return this.getTypesFromExistingModel(this.model);
    }

    let adTypes = [];

    if (this.isDynamicAdGroupAdCreation()) {
      return this.getDynamicSearchAdTypes();
    }

    if (this.showInline) {
      _.each(campaignAdTypesMapping, (value) => {
        adTypes = _.union(adTypes, value({
          appInstallAdsAllowed: this.appInstallAdsAllowed,
          isSTADisabled: this.isSTADisabled,
          isForVNext: this.isForVNext,
        }));
      });

      _.each(campaignSubTypeAdTypesMapping, (value) => {
        adTypes = _.union(adTypes, value());
      });

      // when the adgroup type is not selected, need to add DSA to ad types
      if (this.adGroupType() !== adGroupTypeEnum.SearchStandard) {
        adTypes = _.union(adTypes, this.getDynamicSearchAdTypes());
      }

      return adTypes;
    }

    return campaignAdTypesMapping[this.scope().campaignType || campaignTypes.search]({
      appInstallAdsAllowed: this.appInstallAdsAllowed,
      isForVNext: this.isForVNext,
    });
  }

  getTypesFromExistingModel(model) {
    return (this.isMigratingToEXTA ? [adTypeEnum.expandedText] : []).concat(model.Type);
  }

  isSmartShoppingCampaign() {
    const checkCampaignSubType = this.scope().campaignSubType === 'ShoppingSmartAds' || this.scope().campaignSubType === 12;

    return pageContext.Permissions.IsSmartShoppingCampaignEnabled && checkCampaignSubType;
  }
}
